import type { Workout } from '@onflow/shared';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useWorkoutHistory } from '../hooks/useStats.js';
import { formatDuration } from '../lib/format.js';

export function Calendar() {
  const history = useWorkoutHistory(100);
  const groups = useMemo(() => {
    const byDate = new Map<string, Workout[]>();
    for (const workout of history.data?.pages.flatMap((page) => page.items) ?? []) {
      const key = new Date(workout.date).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      const group = byDate.get(key) ?? [];
      group.push(workout);
      byDate.set(key, group);
    }
    return [...byDate.entries()];
  }, [history.data]);

  if (history.isLoading) return <section className="empty-state card">Loading calendar…</section>;
  if (history.isError) {
    return (
      <section className="empty-state card" role="alert">
        Couldn&apos;t load your workout calendar.
      </section>
    );
  }

  return (
    <section className="fade-in">
      <div className="page-header">
        <h1 className="page-title">Calendar</h1>
        <p className="page-subtitle">Completed workouts by day</p>
      </div>
      {groups.length === 0 ? (
        <div className="empty-state card">
          <h3>No completed workouts yet</h3>
          <Link className="btn btn-primary" to="/workout/new">
            Start a workout
          </Link>
        </div>
      ) : (
        groups.map(([date, workouts]) => (
          <section key={date} style={{ marginBottom: 22 }} aria-label={date}>
            <h2 style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 8 }}>{date}</h2>
            {workouts.map((workout) => (
              <Link key={workout.id} to={`/history/${workout.id}`} className="workout-card">
                <strong>{workout.title}</strong>
                <div className="workout-card-stats">
                  <span>{formatDuration(workout.duration)}</span>
                  <span>{workout.totalSets} sets</span>
                </div>
              </Link>
            ))}
          </section>
        ))
      )}
      {history.hasNextPage && (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => void history.fetchNextPage()}
          disabled={history.isFetchingNextPage}
        >
          {history.isFetchingNextPage ? 'Loading…' : 'Load older dates'}
        </button>
      )}
    </section>
  );
}
